import { fmtDate, fmtDateTime } from '../../lib/format';
import React, { useEffect, useState } from 'react';
import { Table, Button, Tag, message, Popconfirm, Space, Typography, Empty, Modal, Form, Input, DatePicker } from 'antd';
import { CheckCircleOutlined, EditOutlined, ReloadOutlined, ClockCircleOutlined } from '@ant-design/icons';
import type { Dayjs } from 'dayjs';
import {
  getPendingProducts, approveProduct, requestChanges, scheduleProduct, type PartnerProduct,
} from '../../services/creditProductService';

const { Title, Paragraph } = Typography;

const shortId = (id?: string) => (id ? id.replace(/-/g, '').slice(0, 8) : '—');
const fmtMoney = (n: number) => new Intl.NumberFormat().format(n);

// Category presentation. Falls back to CREDIT so pre-generic products render unchanged.
const catOf = (p: PartnerProduct) => (p.category || 'CREDIT').toUpperCase();
const CAT_META: Record<string, { label: string; color: string }> = {
  CREDIT: { label: 'Loan / advance', color: 'blue' },
  INSURANCE: { label: 'Insurance', color: 'gold' },
  HEALTH: { label: 'Health', color: 'green' },
  BENEFIT: { label: 'Benefit', color: 'purple' },
  WELLNESS: { label: 'Wellness', color: 'cyan' },
};
const catMeta = (p: PartnerProduct) => CAT_META[catOf(p)] || { label: catOf(p).toLowerCase(), color: 'default' };
const humanize = (s: string) => s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

// Category-aware terms: loans show rate/amount/term; everything else reads its config blob
// (insurance: plan_type, employee_premium, employer_contribution, coverage).
const renderTerms = (p: PartnerProduct) => {
  if (catOf(p) === 'CREDIT') {
    return (
      <div style={{ fontSize: 13 }}>
        <div>{p.default_annual_rate}% · {p.interest_method}</div>
        <div style={{ color: '#888' }}>
          {p.currency} {fmtMoney(p.min_amount)} – {fmtMoney(p.max_amount)} · {p.max_term_months} mo
        </div>
      </div>
    );
  }
  const c = (p.config ?? {}) as Record<string, unknown>;
  const num = (v: unknown) => (typeof v === 'number' ? v : Number(v) || 0);
  const bits: string[] = [];
  if (c.plan_type) bits.push(humanize(String(c.plan_type)));
  if (c.employee_premium != null) bits.push(`Emp ${p.currency} ${fmtMoney(num(c.employee_premium))}/mo`);
  if (c.employer_contribution != null) bits.push(`Employer ${p.currency} ${fmtMoney(num(c.employer_contribution))}/mo`);
  return (
    <div style={{ fontSize: 13 }}>
      <div>{bits.length ? bits.join(' · ') : '—'}</div>
      {c.coverage ? <div style={{ color: '#888' }}>{String(c.coverage)}</div> : null}
    </div>
  );
};

const PartnerProductReviewPage: React.FC = () => {
  const [rows, setRows] = useState<PartnerProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState<string | null>(null);
  const [changesFor, setChangesFor] = useState<PartnerProduct | null>(null);
  const [scheduleForP, setScheduleForP] = useState<PartnerProduct | null>(null);
  const [scheduleAt, setScheduleAt] = useState<Dayjs | null>(null);
  const [form] = Form.useForm();

  const load = async () => {
    setLoading(true);
    try {
      setRows(await getPendingProducts());
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Could not load the review queue');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onApprove = async (id: string) => {
    setActing(id);
    try {
      await approveProduct(id);
      message.success('Product approved and published.');
      await load();
    } catch (e: any) {
      // 403 = four-eyes (you submitted/created it); 409 = concurrent change.
      message.error(e?.response?.data?.message || 'Approve failed');
    } finally {
      setActing(null);
    }
  };

  const submitSchedule = async () => {
    if (!scheduleForP || !scheduleAt) {
      message.warning('Pick a go-live date and time.');
      return;
    }
    if (!scheduleAt.isAfter(new Date())) {
      message.warning('The go-live time must be in the future.');
      return;
    }
    setActing(scheduleForP.id);
    try {
      await scheduleProduct(scheduleForP.id, scheduleAt.toISOString());
      message.success(`Scheduled to go live ${scheduleAt.format('D MMM YYYY, HH:mm')}.`);
      setScheduleForP(null);
      setScheduleAt(null);
      await load();
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Could not schedule the product');
    } finally {
      setActing(null);
    }
  };

  const submitChanges = async () => {
    if (!changesFor) return;
    const { note } = await form.validateFields();
    setActing(changesFor.id);
    try {
      await requestChanges(changesFor.id, note);
      message.success('Sent back to the partner for changes.');
      setChangesFor(null);
      form.resetFields();
      await load();
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Could not request changes');
    } finally {
      setActing(null);
    }
  };

  const columns = [
    {
      title: 'Product',
      key: 'product',
      render: (_: unknown, p: PartnerProduct) => (
        <div>
          <div style={{ fontWeight: 600 }}>{p.name}</div>
          <div style={{ color: '#888', fontSize: 12 }}>{p.code}</div>
        </div>
      ),
    },
    { title: 'Partner', key: 'partner', render: (_: unknown, p: PartnerProduct) => <Tag>{shortId(p.partner_id)}</Tag> },
    {
      title: 'Category',
      key: 'category',
      render: (_: unknown, p: PartnerProduct) => {
        const m = catMeta(p);
        return <Tag color={m.color}>{m.label}</Tag>;
      },
    },
    { title: 'Terms', key: 'terms', render: (_: unknown, p: PartnerProduct) => renderTerms(p) },
    { title: 'Submitted', dataIndex: 'submitted_at', key: 'submitted_at', render: (d?: string) => (d ? fmtDate(d) : '—') },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: unknown, p: PartnerProduct) => (
        <Space>
          <Popconfirm title="Approve & publish this product now?" onConfirm={() => onApprove(p.id)} okText="Approve">
            <Button type="primary" size="small" icon={<CheckCircleOutlined />} loading={acting === p.id}>Approve</Button>
          </Popconfirm>
          {/* Scheduling needs a credit.products row; benefits-fulfilled products approve directly. */}
          {(p.fulfillment_domain ?? 'credit') !== 'benefits' && (
            <Button size="small" icon={<ClockCircleOutlined />} onClick={() => { setScheduleForP(p); setScheduleAt(null); }} loading={acting === p.id}>Schedule</Button>
          )}
          <Button size="small" icon={<EditOutlined />} onClick={() => setChangesFor(p)} loading={acting === p.id}>Request changes</Button>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <Title level={3} style={{ marginBottom: 0 }}>Partner Product Review</Title>
          <Paragraph type="secondary">
            Review products submitted by partners — loans, insurance, and other benefits. Approving
            publishes the product so it can be offered to employers. You cannot approve a product you
            submitted or created (four-eyes).
          </Paragraph>
        </div>
        <Button icon={<ReloadOutlined />} onClick={load}>Refresh</Button>
      </div>
      <Table
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={rows}
        pagination={false}
        locale={{ emptyText: <Empty description="No products awaiting review" /> }}
      />

      <Modal
        title={`Request changes — ${changesFor?.name ?? ''}`}
        open={!!changesFor}
        onOk={submitChanges}
        confirmLoading={acting === changesFor?.id}
        onCancel={() => { setChangesFor(null); form.resetFields(); }}
        okText="Send back"
      >
        <Paragraph type="secondary">This sends the product back to the partner as “Changes requested”, with your note. The partner revises it and resubmits.</Paragraph>
        <Form form={form} layout="vertical">
          <Form.Item name="note" label="What needs to change?" rules={[{ required: true, message: 'Please add a note for the partner' }]}>
            <Input.TextArea rows={3} maxLength={500} placeholder="e.g. The max annual rate exceeds our cap; reduce it to 24%. (For insurance: clarify the coverage limits.)" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`Schedule go-live — ${scheduleForP?.name ?? ''}`}
        open={!!scheduleForP}
        onOk={submitSchedule}
        confirmLoading={acting === scheduleForP?.id}
        onCancel={() => { setScheduleForP(null); setScheduleAt(null); }}
        okText="Schedule"
        okButtonProps={{ disabled: !scheduleAt }}
      >
        <Paragraph type="secondary">
          The product is approved now but stays hidden until the date below, when it automatically goes live for employers. You can pull it back to review any time before then.
        </Paragraph>
        <DatePicker
          showTime
          style={{ width: '100%' }}
          value={scheduleAt}
          onChange={setScheduleAt}
          disabledDate={(d) => !!d && d.isBefore(new Date(), 'day')}
          placeholder="Pick a future go-live date & time"
        />
      </Modal>
    </div>
  );
};

export default PartnerProductReviewPage;
